import { getCurrentInstance } from 'vue';
import { Card, SprintState, List, ListBehavior } from '@superthread-com/api';
import { handleAddEventBoardView } from '@/utilities/moveCard';
import useTranslate from '@/utilities/composables/useTranslate';
import { getSprintKey, getSprintListKey } from '@/utilities/sprintKeys';
import constants from '@/utilities/constants';
import getDashedTitle from '@/utilities/getDashedTitle';

type SprintList = List & { orginalId: string };

export default function useMoveToSprint() {
  const _this = getCurrentInstance()?.proxy as any;
  if (!_this) return;

  const { translate } = useTranslate();

  const getDestinationListId = (sprint: any, forUpdateRequest = false) => {
    const id = forUpdateRequest ? 'orginalId' : 'id';

    return sprint.state === SprintState.Completed
      ? (sprint.lists || []).find((s: SprintList) => s.behavior === ListBehavior.Completed)?.[id]
      : sprint.lists[0]?.[id];
  };

  const moveToSprint = async (sprintId: string, card: Card, showOpenSprint = false) => {
    const sprint = _this.$store.getters.getFlatBoards[sprintId];
    if (!sprint) return;

    // Prevent moving to same sprint
    if (card.sprint_id === sprint.id.split('s')[1]) return false;

    const generatedCardSprintId = getSprintKey(card.project_id, card.sprint_id);
    const originalListId = card.sprint_id
      ? getSprintListKey(card.project_id, card.sprint_id, card.list_id)
      : card.list_id;
    const originalBoardId = card.sprint_id ? generatedCardSprintId : card.board_id;

    const destinationListId = getDestinationListId(sprint, false);
    const listIdForRequest = getDestinationListId(sprint, true);

    handleAddEventBoardView({
      newIndex: 0,
      oldIndex: 0,
      cardId: card.id,
      listId: destinationListId,
      originalListId,
      destinationBoardId: sprint.id,
      originalBoardId,
      storeGetters: _this.$store.getters,
      storeDispatch: _this.$store.dispatch,
      order: 0,
      originalProjectId: card.project_id,
      destinationProjectId: sprint.project_id,
    });

    _this.$store.dispatch('removeCardFromGroupByBoard', { boardId: originalBoardId, card });
    _this.$store.dispatch('addCardToGroupByBoard', { boardId: sprint.id, card });

    await _this.$store
      .dispatch('updateCard', {
        cardId: card.id,
        card: {
          position: 0,
          list_id: listIdForRequest,
          sprint_id: sprint.orginalId,
          project_id: card.project_id,
        },
        oldListId: originalListId,
      })
      .then(() => {
        _this.$store.dispatch('addUiNotification', {
          message: `${translate('moveCardIntoSprint', { sprintName: sprint.title })}`,
          icon: 'info',
          duration: 5000,
          ...(showOpenSprint && {
            labels: [
              {
                text: translate('open'),
                type: constants.uiNotificationElements.button,
                callback: () => {
                  const teamSlug = _this.$store.getters.getTeamSlug;
                  const itemRoute = `sprint-${sprintId}`;

                  _this.$store.dispatch('setCurrentBoardId', sprint.id);

                  const sprintPath = `/${teamSlug}/${itemRoute}-${getDashedTitle(sprint.title)}`;
                  if (_this.$route.path != sprintPath)
                    _this.$router.push({
                      path: sprintPath,
                    });
                },
                buttonStyle: 'important',
              },
            ],
          }),
        });
      })
      .catch((err: Error) => {
        handleAddEventBoardView({
          newIndex: 0,
          oldIndex: 0,
          cardId: card.id,
          listId: originalListId,
          originalListId: destinationListId,
          destinationBoardId: originalBoardId,
          originalBoardId: sprint.id,
          storeGetters: _this.$store.getters,
          storeDispatch: _this.$store.dispatch,
          order: 0,
          originalProjectId: card.project_id,
          destinationProjectId: sprint.project_id,
        });
        throw err;
      });
  };

  const getStatusLabelText = (status: string | null): string => {
    switch (status) {
      case SprintState.Active:
        return translate('activeSprintText');
      case SprintState.Planned:
        return translate('next');
      case SprintState.Canceled:
        return translate('cancelled');
      default:
        return translate('completed');
    }
  };

  return {
    getDestinationListId,
    moveToSprint,
    getStatusLabelText,
  };
}
